import React, { useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Printer, Download, Ruler, Lightbulb, Cable, Zap, 
  AlertTriangle, CheckCircle2, FileText, ClipboardList
} from 'lucide-react';
import { format } from 'date-fns';

export default function InstallPlan({ project }) {
  const printRef = useRef(null);
  
  const { sections = [], jumps = [], parameters = {}, pricing = {}, power_plan = {} } = project;
  
  // Calculations
  const totalLightingFeet = sections.reduce((sum, s) => sum + (s.length_feet || 0), 0);
  const totalJumpFeet = jumps.reduce((sum, j) => sum + (j.length_feet || 0), 0);
  const totalWireFeet = totalLightingFeet + totalJumpFeet;
  const spacingInches = parameters.light_spacing_inches || 12;
  const totalPucks = Math.ceil((totalLightingFeet * 12) / spacingInches);
  const wattsPerPuck = 0.5;
  const totalWatts = totalPucks * wattsPerPuck;
  const transformerWatts = parameters.transformer_watts || 200;
  const transformersNeeded = Math.ceil(totalWatts / (transformerWatts * 0.8));

  // Labor time calculation (in hours) - matches MaterialsList detailed breakdown
  const setupTime = 1.0; // Setup & Planning
  const sectionInstallTime = sections.length * 0.5; // Section Installation
  const puckInstallTime = (totalPucks / 10) * 0.25; // Puck mounting (15 min per 10 pucks)
  const jumpTime = jumps.length * 0.5; // Jump wire runs (30 min per jump)
  const totalLaborHours = setupTime + sectionInstallTime + puckInstallTime + jumpTime;

  // Pricing calculations
  const laborCost = totalLaborHours * (pricing.labor_rate_per_hour || 85);
  const lightsCost = totalPucks * (pricing.light_cost_per_unit || 3.5);
  const wireCost = totalWireFeet * (pricing.wire_cost_per_foot || 0.75);
  const transformerCost = transformersNeeded * (pricing.transformer_cost || 150);
  const miscCost = pricing.misc_materials || 50;
  const subtotal = laborCost + lightsCost + wireCost + transformerCost + miscCost;
  const discount = subtotal * ((pricing.discount_percent || 0) / 100);
  const total = subtotal - discount;

  const getMountingNotes = () => {
    const notes = [];
    switch (parameters.mounting_method) {
      case 'track':
        notes.push('Cut aluminum track to length before installation');
        notes.push('Pre-drill mounting holes every 24"');
        notes.push('Use stainless steel screws for durability');
        break;
      case 'clips':
        notes.push('Space clips every 12" for secure hold');
        notes.push('Ensure clips are rated for outdoor use');
        break;
      case 'adhesive':
        notes.push('Clean surface thoroughly before applying');
        notes.push('Apply in temperatures above 50°F');
        notes.push('Use outdoor-rated adhesive only');
        break;
      case 'adhesive_screw':
        notes.push('Clean surface thoroughly before applying adhesive');
        notes.push('Use screws at stress points for additional security');
        notes.push('Apply adhesive in temperatures above 50°F');
        break;
      case 'channel':
        notes.push('Route channel to conceal wiring');
        notes.push('Ensure proper drainage for channels');
        break;
      default:
        notes.push('Follow manufacturer mounting guidelines');
        break;
    }
    return notes;
  };

  const getWireNotes = () => {
    const notes = [];
    if (parameters.wire_gauge === '22awg') {
      notes.push('22 AWG suitable for runs under 25 ft only');
    } else if (parameters.wire_gauge === '16awg') {
      notes.push('Use 16 AWG for all runs over 50 ft');
    }
    notes.push('All connections must be waterproof');
    notes.push('Use silicone sealant on all junction points');
    return notes;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-8">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-3 print:hidden">
        <Button onClick={handlePrint} className="bg-slate-900 hover:bg-slate-800">
          <Printer className="w-4 h-4 mr-2" />
          Print Install Sheet
        </Button>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="space-y-6 print:space-y-4">
        {/* Page 1: Project Overview */}
        <Card className="print:shadow-none print:border-2">
          <CardHeader className="bg-slate-900 text-white print:bg-white print:text-slate-900 print:border-b-2">
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-4">
                <img 
                  src="/SNO-LightingLogo.png" 
                  alt="SNO-Lighting Logo" 
                  className="h-12 w-auto"
                />
                <div>
                  <CardTitle className="text-2xl">{project.name || 'Untitled Project'}</CardTitle>
                  <p className="text-slate-300 print:text-slate-600 mt-1">{project.address}</p>
                  {project.customer_name && (
                    <p className="text-slate-300 print:text-slate-600 text-sm mt-1">Customer: {project.customer_name}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-300 print:text-slate-600">Installation Plan</p>
                <p className="font-medium">{format(new Date(), 'MMM d, yyyy')}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-amber-50 rounded-xl text-center">
                <Ruler className="w-5 h-5 mx-auto text-amber-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{totalLightingFeet.toFixed(1)}'</p>
                <p className="text-xs text-slate-600">Total Lighting</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <Lightbulb className="w-5 h-5 mx-auto text-blue-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{totalPucks}</p>
                <p className="text-xs text-slate-600">Total Pucks</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <Cable className="w-5 h-5 mx-auto text-green-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{totalWireFeet.toFixed(1)}'</p>
                <p className="text-xs text-slate-600">Total Wire</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl text-center">
                <Zap className="w-5 h-5 mx-auto text-purple-600 mb-2" />
                <p className="text-2xl font-bold text-slate-900">{totalWatts.toFixed(0)}W</p>
                <p className="text-xs text-slate-600">Power Draw</p>
              </div>
            </div>

            {/* Parameters Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="text-xs text-slate-500">Spacing</p>
                <p className="font-medium">{spacingInches}" centers</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Light Type</p>
                <p className="font-medium">
                  {parameters.light_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'RGBIC Puck'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Mounting</p>
                <p className="font-medium">
                  {parameters.mounting_method?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Adhesive Tape & Screw'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Wire Gauge</p>
                <p className="font-medium">{parameters.wire_gauge?.toUpperCase() || '18 AWG'}</p>
              </div>
            </div>

            {/* Sections Table */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                Lighting Sections
              </h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Section Name</TableHead>
                    <TableHead className="text-right">Length</TableHead>
                    <TableHead className="text-right">Pucks</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sections.map((section, idx) => (
                    <TableRow key={section.id}>
                      <TableCell>
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white" 
                             style={{ backgroundColor: section.color }}>
                          {String.fromCharCode(65 + idx)}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{section.name}</TableCell>
                      <TableCell className="text-right">{section.length_feet?.toFixed(1)}'</TableCell>
                      <TableCell className="text-right">
                        {Math.ceil((section.length_feet * 12) / spacingInches)}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{section.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-medium">
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell className="text-right">{totalLightingFeet.toFixed(1)}'</TableCell>
                    <TableCell className="text-right">{totalPucks}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Jumps Table */}
            {jumps.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Cable className="w-5 h-5 text-blue-600" />
                  Jumps & Extensions
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Length</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jumps.map((jump, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{sections.find(s => s.id === jump.from_section)?.name || jump.from_section}</TableCell>
                        <TableCell>{sections.find(s => s.id === jump.to_section)?.name || jump.to_section}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{jump.type?.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{jump.length_feet}'</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-slate-50 font-medium">
                      <TableCell colSpan={3}>Total Extension Wire</TableCell>
                      <TableCell className="text-right">{totalJumpFeet}'</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Power Plan */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                Power Plan
              </h3>
              <div className="grid md:grid-cols-2 gap-4 p-4 bg-purple-50 rounded-xl">
                <div>
                  <p className="text-sm text-slate-600">Total Power Draw</p>
                  <p className="text-xl font-bold">{totalWatts.toFixed(0)}W</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Transformers Required</p>
                  <p className="text-xl font-bold">{transformersNeeded} × {transformerWatts}W</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-slate-600">Power Source</p>
                  <p className="font-medium">{parameters.power_source_location || 'Not specified'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Page Break for Print */}
        <div className="print:break-before-page" />

        {/* Page 2: Installation Notes & Checklist */}
        <Card className="print:shadow-none print:border-2">
          <CardHeader className="bg-slate-900 text-white print:bg-white print:text-slate-900 print:border-b-2">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Installation Notes & Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Installation Notes */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Mounting Notes
                </h4>
                <ul className="space-y-2">
                  {getMountingNotes().map((note, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-amber-600 mt-1">•</span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-blue-600" />
                  Wiring Notes
                </h4>
                <ul className="space-y-2">
                  {getWireNotes().map((note, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-blue-600 mt-1">•</span>
                      {note}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Separator />

            {/* Pre-Install Checklist */}
            <div>
              <h4 className="font-semibold mb-4">Pre-Installation Checklist</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  'Verify all measurements on-site',
                  'Check power source availability',
                  'Inspect eave condition for mounting',
                  'Confirm transformer location accessible',
                  'Test all lights before installation',
                  'Weather forecast checked',
                  'Ladder/lift equipment secured',
                  'Customer notified of install date'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2">
                    <Checkbox id={`pre-${idx}`} />
                    <label htmlFor={`pre-${idx}`} className="text-sm">{item}</label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Post-Install Checklist */}
            <div>
              <h4 className="font-semibold mb-4">Post-Installation Checklist</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  'All sections illuminating correctly',
                  'No visible wiring from ground',
                  'Connections sealed and waterproofed',
                  'Controller programmed and tested',
                  'Customer walkthrough completed',
                  'Photos taken for documentation',
                  'Cleanup completed',
                  'Warranty information provided'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-2">
                    <Checkbox id={`post-${idx}`} />
                    <label htmlFor={`post-${idx}`} className="text-sm">{item}</label>
                  </div>
                ))}
              </div>
            </div>

            {/* Section Legend */}
            <div>
              <h4 className="font-semibold mb-3">Section Legend</h4>
              <div className="flex flex-wrap gap-3">
                {sections.map((section, idx) => (
                  <div key={section.id} className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: section.color }} />
                    <span className="text-sm font-medium">{section.name}</span>
                    <span className="text-xs text-slate-500">{section.length_feet?.toFixed(1)}'</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Page Break for Print */}
        <div className="print:break-before-page" />

        {/* Customer Quote */}
        <Card className="print:shadow-none print:border-2">
          <CardHeader className="bg-amber-600 text-white print:bg-white print:text-slate-900 print:border-b-2">
            <div className="flex justify-between items-center">
              <CardTitle>Customer Quote</CardTitle>
              <p className="text-sm opacity-80">Valid for 30 days</p>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-500">Project</p>
                <p className="font-semibold text-lg">{project.name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Address</p>
                <p className="font-semibold">{project.address || 'Not specified'}</p>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <p className="font-medium">Installation Labor</p>
                    <p className="text-sm text-slate-500">Professional installation of permanent lighting</p>
                  </TableCell>
                  <TableCell className="text-right">{totalLaborHours.toFixed(1)} hrs</TableCell>
                  <TableCell className="text-right">${(pricing.labor_rate_per_hour || 85).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium">${laborCost.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <p className="font-medium">{parameters.light_type?.replace(/_/g, ' ').toUpperCase() || 'RGBW Puck'} Lights</p>
                    <p className="text-sm text-slate-500">At {spacingInches}" spacing</p>
                  </TableCell>
                  <TableCell className="text-right">{totalPucks} pcs</TableCell>
                  <TableCell className="text-right">${(pricing.light_cost_per_unit || 3.5).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium">${lightsCost.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <p className="font-medium">Wiring ({parameters.wire_gauge?.toUpperCase() || '18 AWG'})</p>
                    <p className="text-sm text-slate-500">Includes lighting runs and extensions</p>
                  </TableCell>
                  <TableCell className="text-right">{totalWireFeet.toFixed(1)} ft</TableCell>
                  <TableCell className="text-right">${(pricing.wire_cost_per_foot || 0.75).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium">${wireCost.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <p className="font-medium">{transformerWatts}W Transformer</p>
                    <p className="text-sm text-slate-500">Low voltage power supply</p>
                  </TableCell>
                  <TableCell className="text-right">{transformersNeeded}</TableCell>
                  <TableCell className="text-right">${(pricing.transformer_cost || 150).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium">${transformerCost.toFixed(2)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <p className="font-medium">Mounting Hardware & Supplies</p>
                    <p className="text-sm text-slate-500">Track, connectors, sealant, fasteners</p>
                  </TableCell>
                  <TableCell className="text-right">1</TableCell>
                  <TableCell className="text-right">${(pricing.misc_materials || 50).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-medium">${miscCost.toFixed(2)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between py-2 text-green-600">
                    <span>Discount ({pricing.discount_percent}%)</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between py-2 text-lg">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-amber-600">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-dashed">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-slate-500 mb-4">Customer Signature</p>
                  <div className="h-16 border-b border-slate-300" />
                  <p className="text-sm text-slate-500 mt-2">Date: _______________</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-4">Installer Signature</p>
                  <div className="h-16 border-b border-slate-300" />
                  <p className="text-sm text-slate-500 mt-2">Date: _______________</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}